import { gql, useMutation } from '@apollo/client';
import { useEffect, useState, useCallback } from 'react';
import { PasswordInput } from '@carbon/react';
import { useAuth } from '../Utils/authProvider';
import Header from '../Components/header';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from "@iconify/react/dist/iconify.js";
import leftIcon from '@iconify/icons-solar/arrow-left-linear';
import { createSHA256Hash } from '../Utils/cryptoUtils';


const SET_MY_PASSWORD_MUTATION = gql`
  mutation SetMyPassword($oldPassword: String!, $newPassword: String!) {
    setMyPassword(oldPassword: $oldPassword, newPassword: $newPassword)
  }
`;


export default function Settings() {
  const auth = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [passwordChangeEnabled, setPasswordChangeEnabled] = useState(false);
  const [success, setSuccess] = useState(false);
  const [setMyPassword, { loading, error }] = useMutation(SET_MY_PASSWORD_MUTATION);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  // Validation logic (like userEdit)
  const validatePasswordForm = useCallback(() => {
    if (oldPassword === '' && newPassword === '' && confirmPassword === '') {
      setPasswordErrorMessage('');
      setPasswordChangeEnabled(false);
      return false;
    }
    if (oldPassword === '') {
      setPasswordErrorMessage('Altes Passwort ist erforderlich.');
      setPasswordChangeEnabled(false);
      return false;
    }
    if (newPassword === '') {
      setPasswordErrorMessage('Neues Passwort ist erforderlich.');
      setPasswordChangeEnabled(false);
      return false;
    }
    if (confirmPassword === '') {
      setPasswordErrorMessage('Passwortbestätigung ist erforderlich.');
      setPasswordChangeEnabled(false);
      return false;
    }
    if (newPassword !== confirmPassword) {
      setPasswordErrorMessage('Passwörter stimmen nicht überein.');
      setPasswordChangeEnabled(false);
      return false;
    }

    // New security checks: min 12 chars, at least one uppercase, at least one digit
    if (newPassword.length < 12) {
      setPasswordErrorMessage('Das neue Passwort muss mindestens 12 Zeichen lang sein.');
      setPasswordChangeEnabled(false);
      return false;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordErrorMessage('Das neue Passwort muss mindestens einen Großbuchstaben enthalten.');
      setPasswordChangeEnabled(false);
      return false;
    }
    if (!/\d/.test(newPassword)) {
      setPasswordErrorMessage('Das neue Passwort muss mindestens eine Ziffer enthalten.');
      setPasswordChangeEnabled(false);
      return false;
    }

    setPasswordErrorMessage('');
    setPasswordChangeEnabled(true);
    return true;
  }, [oldPassword, newPassword, confirmPassword]);

  useEffect(() => {
    validatePasswordForm();
  }, [validatePasswordForm]);

  // Start a 5-second countdown on success, then redirect to /logout
  useEffect(() => {
    let timer;
    if (success) {
      setPasswordErrorMessage('Passwort erfolgreich geändert!');
      setCountdown(5);
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/logout');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [success, navigate]);

  async function handlePasswordChange(event) {
    event.preventDefault();
    if (!validatePasswordForm()) return;
    try {
      const result = await setMyPassword({ variables: { oldPassword: createSHA256Hash(oldPassword), newPassword: createSHA256Hash(newPassword) } });

      // GraphQL-level errors
      if (result?.errors?.length) {
        const msg = result.errors[0]?.message || 'GraphQL error';
        console.error('GraphQL error:', msg);
        setPasswordErrorMessage("Das alte Passwort scheint nicht korrekt zu sein");
        return;
      }

      const data = result?.data;
      if (data && typeof data.setMyPassword !== 'undefined') {
        if (data.setMyPassword) {
          setOldPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setSuccess(true);
          setPasswordErrorMessage('Passwort erfolgreich geändert!');
        } else {
          console.error('Password change failed:', result);
          setPasswordErrorMessage('Passwort konnte nicht geändert werden.');
        }
      } else {
        console.error('Unexpected response structure:', result);
        setPasswordErrorMessage('Das alte Passwort scheint nicht korrekt zu sein.');
      }
    } catch (err) {
      console.error('Mutation threw:', err);
      setPasswordErrorMessage(err?.graphQLErrors?.[0]?.message || err.message || 'Fehler beim Ändern des Passworts.');
    }
  }

  return (
    <>
      <Header />
      <nav className="bg-white shadow">
        <div className="container mx-auto">
          <div className="py-2">
            <div className="flex flex-row items-center w-full py-2 gap-3 text-slate-800">
              <ul className="flex items-center gap-4">
              <li className="text-gray-700 hover:text-gray-900">
                <Link to={'/'} className="flex items-center text-md">
                  <Icon icon={leftIcon} className="inline-block mr-2" width={22} height={22} />
                  Zurück
                </Link>
              </li>
            </ul>
            </div>
          </div>
        </div>
      </nav>
      <section className="container px-4 md:px-0 mx-auto">
        <div className="py-4">
          <div className="max-w-2xl">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">
                        Einstellungen
                    </h1>
            </div>
        <form onSubmit={handlePasswordChange} className="mt-4 space-y-6">
          <PasswordInput
            id="oldPassword"
            labelText="Altes Passwort"
            placeholder="Altes Passwort eingeben"
            className="w-full"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
          <PasswordInput
            id="newPassword"
            labelText="Neues Passwort"
            placeholder="Neues Passwort eingeben"
            className="w-full"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <PasswordInput
            id="confirmPassword"
            labelText="Neues Passwort bestätigen"
            placeholder="Neues Passwort erneut eingeben"
            className="w-full"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
            <p className="text-sm text-red-600 h-5">
              {passwordErrorMessage}
              {success && countdown > 0 ? ` (${countdown} s)` : null}
            </p>
            <button
              type="submit"
              className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${
                passwordChangeEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
              }`}
              disabled={!passwordChangeEnabled || loading}
            >
              Passwort ändern
            </button>
          </div>
        </form>
      </div>
    </div>
        </section>
      </>
  );
}